import React, { memo, useCallback, useMemo } from 'react';
import { Spin, Pagination, Empty } from 'antd';

interface Column<T> {
  key: string;
  title: React.ReactNode;
  sortKey?: string;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  currentPage?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number, pageSize?: number) => void;
  sortOrder?: number | '';
  onSort?: (sortKey: string) => void;
  rowKey?: keyof T | ((item: T, index: number) => string | number);
  className?: string;
  tableClassName?: string;
  emptyText?: string;
  showPagination?: boolean;
  wrapperClassName?: string;
}

/**
 * Reusable data table component with pagination, sorting, and loading state
 *
 * @param columns - Array of column definitions
 * @param data - Array of data items to display
 * @param loading - Show loading spinner
 * @param currentPage - Current page number (0-indexed internally, displayed as 1-indexed)
 * @param pageSize - Number of items per page
 * @param total - Total number of items for pagination
 * @param onPageChange - Callback when page changes
 * @param sortOrder - Current sort order (1 for asc, -1 for desc)
 * @param onSort - Callback when sort header is clicked
 * @param rowKey - Key or function to generate unique row keys
 * @param className - CSS class for the wrapper div
 * @param tableClassName - CSS class for the table element
 * @param emptyText - Text to show when no data
 * @param showPagination - Whether to show pagination (default: true)
 * @param wrapperClassName - CSS class for the outermost wrapper (useful for responsive visibility)
 *
 * @example
 * const columns = [
 *   { key: 'name', title: 'Name', sortKey: 'name' },
 *   { key: 'email', title: 'Email' },
 *   {
 *     key: 'actions',
 *     title: 'Actions',
 *     render: (item) => <ActionButtons onEdit={() => edit(item._id)} />
 *   }
 * ];
 *
 * <DataTable
 *   columns={columns}
 *   data={userData}
 *   loading={loading}
 *   currentPage={currentPage}
 *   total={totalCount}
 *   onPageChange={changePage}
 *   sortOrder={sortOrder}
 *   onSort={sortData}
 *   rowKey="_id"
 * />
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DataTableComponent<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  currentPage = 0,
  pageSize = 10,
  total = 0,
  onPageChange,
  sortOrder,
  onSort,
  rowKey = (_, index) => index,
  className = '',
  tableClassName = 'table',
  emptyText = 'No data available',
  showPagination = true,
  wrapperClassName = '',
}: DataTableProps<T>): React.ReactElement {
  const getRowKey = useCallback((item: T, index: number): string | number => {
    if (typeof rowKey === 'function') {
      return rowKey(item, index);
    }
    return item[rowKey] as string | number;
  }, [rowKey]);

  const sortIcon = useMemo(() => {
    return sortOrder === 1 ? (
      <i className="fa fa-sort-amount-asc" aria-hidden="true"></i>
    ) : (
      <i className="fa fa-sort-amount-desc" aria-hidden="true"></i>
    );
  }, [sortOrder]);

  const renderSortIcon = useCallback((column: Column<T>) => {
    if (!column.sortKey || !onSort) return null;
    return <> {sortIcon}</>;
  }, [onSort, sortIcon]);

  const renderHeaderCell = useCallback((column: Column<T>) => {
    const isSortable = column.sortKey && onSort;

    return (
      <th
        key={column.key}
        scope="col"
        className={`${isSortable ? 'cursor-pointer' : ''} ${column.className || ''}`}
        onClick={isSortable ? () => onSort(column.sortKey!) : undefined}
      >
        {column.title}
        {renderSortIcon(column)}
      </th>
    );
  }, [onSort, renderSortIcon]);

  const renderCell = useCallback((item: T, column: Column<T>, index: number) => {
    if (column.render) {
      return column.render(item, index);
    }
    return item[column.key] as React.ReactNode;
  }, []);

  const renderRow = useCallback((item: T, index: number) => (
    <tr key={getRowKey(item, index)}>
      {columns.map((column) => (
        <td
          key={column.key}
          className={column.className}
          style={{ verticalAlign: 'middle' }}
        >
          {renderCell(item, column, index)}
        </td>
      ))}
    </tr>
  ), [columns, getRowKey, renderCell]);

  const tableContent = (
    <Spin spinning={loading} delay={500}>
      <div className={`box-body ${className}`}>
        <div className="table-responsive">
          <table className={tableClassName}>
            <thead>
              <tr>{columns.map(renderHeaderCell)}</tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map(renderRow)
              ) : (
                <tr>
                  <td colSpan={columns.length}>
                    <Empty description={emptyText} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {showPagination && (
          <Pagination
            style={{ textAlign: 'right' }}
            pageSize={pageSize}
            current={currentPage + 1}
            defaultCurrent={currentPage + 1}
            total={total || 1}
            onChange={onPageChange}
          />
        )}
      </div>
    </Spin>
  );

  if (wrapperClassName) {
    return <div className={wrapperClassName}>{tableContent}</div>;
  }

  return tableContent;
}

// Memoized DataTable component for better performance
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DataTable = memo(DataTableComponent) as typeof DataTableComponent;

export default DataTable;
